import React, { useEffect, useState } from "react";
import validator from "validator";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

const ChangePasswordDialog = ({
  open,
  onClose,
  onSubmit,
  submitting,
  title = "Change password",
  description = "Sets a new password directly. The user will be marked as having a generated password.",
}) => {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      setPassword("");
      setConfirm("");
      setError("");
      setShowPassword(false);
    }
  }, [open]);

  const handleSubmit = () => {
    if (!validator.isStrongPassword(password)) {
      setError(
        "Password must be at least 8 characters and include an uppercase letter, a lowercase letter, a number, and a special character"
      );
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }
    setError("");
    onSubmit(password);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "0.75rem",
          border: "1px solid hsl(140 8% 88%)",
          backgroundColor: "#ffffff",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
          fontSize: 16,
          color: "hsl(150 30% 12%)",
          pb: 1,
        }}
      >
        {title}
      </DialogTitle>
      <DialogContent>
        <Typography
          sx={{
            fontSize: 13,
            fontFamily: "Segoe UI Regular, sans-serif, system-ui",
            color: "hsl(150 10% 46%)",
            mb: 2,
          }}
        >
          {description}
        </Typography>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="New password"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
            fullWidth
            size="small"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "calc(-2px + 0.75rem)",
                fontSize: 13,
                fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                "& fieldset": { borderColor: "hsl(140 8% 88%)" },
                "&:hover fieldset": { borderColor: "hsl(140 8% 82%)" },
              },
              "& .MuiInputLabel-root": {
                fontSize: 13,
                fontFamily: "Segoe UI Regular, sans-serif, system-ui",
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setShowPassword((v) => !v)}
                    edge="end"
                  >
                    {showPassword ? (
                      <VisibilityOffIcon fontSize="small" />
                    ) : (
                      <VisibilityIcon fontSize="small" />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="Confirm password"
            type={showPassword ? "text" : "password"}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            fullWidth
            size="small"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "calc(-2px + 0.75rem)",
                fontSize: 13,
                fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                "& fieldset": { borderColor: "hsl(140 8% 88%)" },
                "&:hover fieldset": { borderColor: "hsl(140 8% 82%)" },
              },
              "& .MuiInputLabel-root": {
                fontSize: 13,
                fontFamily: "Segoe UI Regular, sans-serif, system-ui",
              },
            }}
          />
          {error && (
            <Typography sx={{ color: "hsl(0 72% 51%)", fontSize: 13 }}>
              {error}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{ px: 3, pb: 2, borderTop: "1px solid hsl(140 8% 88%)", pt: 1.5 }}
      >
        <Button
          onClick={onClose}
          disabled={submitting}
          disableRipple
          sx={{
            textTransform: "none",
            fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
            fontSize: 13,
            color: "hsl(150 10% 46%)",
            borderRadius: "calc(-2px + 0.75rem)",
            "&:hover": {
              backgroundColor: "hsl(140 10% 95%)",
              color: "hsl(150 30% 12%)",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disableElevation
          disableRipple
          onClick={handleSubmit}
          disabled={submitting || !password || !confirm}
          sx={{
            textTransform: "none",
            fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
            fontSize: 13,
            backgroundColor: "hsl(150 38% 17%)",
            color: "hsl(80 97% 72%)",
            borderRadius: "calc(-2px + 0.75rem)",
            transition: "opacity 0.15s ease",
            "&:hover": { backgroundColor: "hsl(150 38% 17%)", opacity: 0.9 },
            "&:disabled": {
              backgroundColor: "hsla(150, 38%, 17%, 0.4)",
              color: "hsla(80, 97%, 72%, 0.6)",
            },
          }}
        >
          {submitting ? "Saving…" : "Set password"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangePasswordDialog;
